import { useTranslations } from '@aikoa/i18n/react'
import { randomUUID } from 'expo-crypto'
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { type Action } from '~/components/Actions'
import { SectionContent } from '~/components/layout/Sections.tsx'
import { Heading1 } from '~/components/typography/Titles.tsx'
import { Button } from '~/components/ui/button.tsx'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog.tsx'

export type AlertContextType = {
  showAlert: (alert: Alert) => void
  closeAlert: () => void
}

type OnPressAction = (args: AlertContextType) => unknown

export type AlertAction = Omit<Action, 'onPress' | 'key'> & {
  key?: string
  shouldClose?: boolean
  buttonClassName?: string
  onPress: OnPressAction
}

export type AlertType = 'info' | 'error' | 'success' | 'confirm'

export type AlertContent =
  | {
      title: string
      description: string
    }
  | {
      children: ReactNode
    }

export type Alert = {
  id?: string
  type: AlertType
  actions?: AlertAction[]
  cancelButton?: boolean | (Omit<AlertAction, 'onPress'> & { onPress?: OnPressAction })
} & AlertContent

const AlertContext = createContext<AlertContextType>({
  showAlert: () => {
    // eslint-disable-next-line i18next/no-literal-string
    throw new Error('AlertProvider not found')
  },
  closeAlert: () => {},
})

const alertIds = new WeakMap<Alert, string>()

export const AlertProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslations('common')
  const [alerts, setAlerts] = useState<(Alert & { id: string })[]>([])

  const showAlert = (alert: Alert) => {
    const id = alertIds.get(alert) ?? alert.id ?? randomUUID()
    alert.id = id
    alertIds.set(alert, id)

    setAlerts((alerts) => [...alerts, { ...alert, id }])
  }

  const alert = useMemo(() => alerts.at(-1), [alerts])

  const closeAlert = useCallback(() => {
    const alert = alerts.at(-1)

    if (alert) {
      alertIds.delete(alert)
      setAlerts((alerts) => alerts.filter((a) => a.id !== alert.id))
    }
  }, [alerts])

  const cancelProps = useMemo(
    () =>
      typeof alert?.cancelButton === 'object'
        ? alert.cancelButton
        : alert?.cancelButton !== false
          ? ({
              key: 'cancel',
              label:
                alert?.type === 'confirm' ? t(($) => $.actions.cancel) : t(($) => $.actions.ok),
              buttonSize: 'default' as const,
              buttonVariant:
                alert?.type === 'confirm'
                  ? ('ghost' as const)
                  : !(alert?.actions ?? [])?.length
                    ? ('outline' as const)
                    : ('primary' as const),
              onPress: closeAlert,
            } satisfies AlertAction)
          : undefined,
    [alert, closeAlert, t]
  )

  return (
    <AlertContext.Provider value={{ showAlert, closeAlert }}>
      {children}
      {!!alert && (
        <Dialog
          open={!!alert}
          onOpenChange={(next) => {
            if (!next) {
              if (alert.cancelButton === false && (alert.actions?.length ?? 0) > 0) {
                const primary = alert.actions![0]

                void Promise.resolve(primary.onPress({ showAlert, closeAlert })).then(() => {
                  if (primary.shouldClose ?? true) {
                    closeAlert()
                  }
                })

                return
              }

              closeAlert()
            }
          }}
          key={alert.id}>
          <DialogContent
            className="min-w-100 w-full max-w-lg flex-initial gap-6 py-8"
            showCloseButton={false}>
            {'title' in alert && (
              <DialogHeader className="gap-2">
                <DialogTitle className="px-0 text-center">
                  <Heading1 className="text-center">{alert.title}</Heading1>
                </DialogTitle>
                <SectionContent className="items-center text-pretty px-4 text-center">
                  {alert.description}
                </SectionContent>
              </DialogHeader>
            )}
            {'children' in alert && alert.children}
            <DialogFooter className="mt-4 flex-col gap-6">
              {alert.actions?.map((action, index) => (
                <Button
                  variant={action.buttonVariant ?? (index === 0 ? 'primary' : 'default')}
                  key={action.label as string}
                  onPress={async () => {
                    await action.onPress({ showAlert, closeAlert })

                    if (action.shouldClose ?? true) {
                      closeAlert()
                    }
                  }}
                  size={action.buttonSize ?? ('default' as const)}
                  className={action.buttonClassName}>
                  {action.label}
                </Button>
              ))}
              {cancelProps && (
                <Button
                  variant={cancelProps.buttonVariant ?? ('primary' as const)}
                  size={cancelProps.buttonSize ?? ('default' as const)}
                  className={cancelProps.className}
                  onPress={async () => {
                    const onPress = cancelProps.onPress ?? closeAlert
                    await onPress({ showAlert, closeAlert })

                    if (cancelProps.shouldClose ?? true) {
                      closeAlert()
                    }
                  }}>
                  {cancelProps.label}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AlertContext.Provider>
  )
}

export const useAlert = () => {
  return useContext(AlertContext)
}
